import { useState, useEffect, useCallback } from 'react';
import { useApiClient } from '../../hooks/use-api-client.js';
import { CopyButton } from '../../components/ui/copy-button/copy-button.js';

interface ApiKeyListItem {
  keyId: string;
  prefix: string;
  deviceName: string;
  createdAt: string;
  lastUsed: string;
  revoked: boolean;
}

interface ApiKeysData {
  keys: ApiKeyListItem[];
  activeCount: number;
  maxKeys: number;
}

type PageState = 'loading' | 'loaded' | 'error';

function formatRelativeDate(isoDate: string): string {
  const date = new Date(isoDate);
  if (isNaN(date.getTime())) return 'Unknown';

  const now = Date.now();
  const diffMs = Math.max(0, now - date.getTime()); // clamp future dates to 0
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  // Dates older than 30 days: show absolute date
  if (diffDays > 30) {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    }).format(date);
  }

  const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' });

  if (diffDays > 0) return rtf.format(-diffDays, 'day');
  if (diffHours > 0) return rtf.format(-diffHours, 'hour');
  if (diffMinutes > 0) return rtf.format(-diffMinutes, 'minute');
  return rtf.format(-diffSeconds, 'second');
}

export function ApiKeysSection() {
  const { fetchApi } = useApiClient();
  const [pageState, setPageState] = useState<PageState>('loading');
  const [data, setData] = useState<ApiKeysData | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [revokingKeyId, setRevokingKeyId] = useState<string | null>(null);
  const [revokeInFlight, setRevokeInFlight] = useState<string | null>(null);
  const [revokeSuccess, setRevokeSuccess] = useState<string | null>(null);
  const [revokeError, setRevokeError] = useState<string | null>(null);

  const loadKeys = useCallback(async () => {
    try {
      const res = await fetchApi('/v1/api-keys');
      if (!res.ok) {
        const body = await res.json();
        setErrorMessage(body?.error?.message ?? 'Failed to load API keys');
        setPageState('error');
        return;
      }
      const body = await res.json();
      setData(body.data);
      setPageState('loaded');
    } catch {
      setErrorMessage('Failed to load API keys');
      setPageState('error');
    }
  }, [fetchApi]);

  useEffect(() => {
    loadKeys();
  }, [loadKeys]);

  const handleRevoke = useCallback(
    async (keyId: string) => {
      if (revokeInFlight === keyId) return; // guard against double-click on same key
      setRevokeInFlight(keyId);
      setRevokeError(null);
      setRevokeSuccess(null);

      try {
        const res = await fetchApi(`/v1/api-keys/${keyId}/revoke`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        });

        if (!res.ok) {
          const body = await res.json();
          const msg = body?.error?.message ?? 'Failed to revoke key';
          setRevokeError(typeof msg === 'string' ? msg.slice(0, 200) : 'Failed to revoke key');
          setRevokingKeyId(null);
          setRevokeInFlight(null);
          setTimeout(() => setRevokeError(null), 5000);
          return;
        }

        // Update local state
        setData((prev) => {
          if (!prev) return prev;
          const updatedKeys = prev.keys.map((k) =>
            k.keyId === keyId ? { ...k, revoked: true } : k,
          );
          return {
            ...prev,
            keys: updatedKeys,
            activeCount: updatedKeys.filter((k) => !k.revoked).length,
          };
        });
        setRevokeSuccess(keyId);
        setRevokingKeyId(null);
        setRevokeInFlight(null);
        setTimeout(() => setRevokeSuccess(null), 3000);
      } catch {
        setRevokeError('Failed to revoke key');
        setRevokingKeyId(null);
        setRevokeInFlight(null);
        setTimeout(() => setRevokeError(null), 5000);
      }
    },
    [fetchApi, revokeInFlight],
  );

  // Loading state
  if (pageState === 'loading') {
    return (
      <div className="flex items-center justify-center py-8" role="status" aria-busy="true">
        <div className="h-5 w-5 animate-spin rounded-full border-2 border-secondary border-t-primary" />
        <span className="ml-3 text-[13px] text-secondary">Loading connected devices…</span>
      </div>
    );
  }

  // Error state
  if (pageState === 'error') {
    return (
      <div className="rounded-md border border-accent-red/30 bg-accent-red/[0.06] p-3 text-[13px] text-accent-red">
        {errorMessage}
      </div>
    );
  }

  const keys = data?.keys ?? [];
  const activeCount = data?.activeCount ?? 0;
  const maxKeys = data?.maxKeys ?? 5;

  return (
    <div data-onboarding="settings-apikeys">
      <div className="mb-4 flex items-baseline justify-between">
        <p className="text-[13px] text-secondary">
          Each connected agent (Claude Code, Cursor, etc.) gets a device session issued by the
          device-code login. Revoke any session you no longer recognize.
        </p>
        <span className="font-mono text-[11px] tracking-[0.04em] text-muted">
          {activeCount} / {maxKeys} active
        </span>
      </div>

      {/* Revoke error */}
      {revokeError && (
        <div className="mt-3 rounded-md border border-accent-red/30 bg-accent-red/[0.06] p-3 text-[13px] text-accent-red">
          {revokeError}
        </div>
      )}

      {/* Empty state */}
      {keys.length === 0 && (
        <div className="mt-4 rounded-md border border-white/[0.06] bg-canvas p-6 text-center">
          <p className="text-[13px] text-secondary">
            No devices connected yet. Run{' '}
            <code className="rounded-[3px] border border-white/[0.06] bg-[#0c0c0c] px-1.5 py-0.5 font-mono text-[12px] text-accent-cyan">
              agentbrush_login
            </code>{' '}
            from your CLI agent to create your first session.
          </p>
        </div>
      )}

      {/* Key list */}
      {keys.length > 0 && (
        <div className="mt-4 overflow-hidden rounded-lg border border-border bg-surface">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-border bg-surface-elevated">
                <th className="px-4 py-3 font-medium text-muted">Key</th>
                <th className="px-4 py-3 font-medium text-muted">Device</th>
                <th className="px-4 py-3 font-medium text-muted">Created</th>
                <th className="px-4 py-3 font-medium text-muted">Last Used</th>
                <th className="px-4 py-3 font-medium text-muted">Status</th>
                <th className="px-4 py-3 font-medium text-muted" />
              </tr>
            </thead>
            <tbody>
              {keys.map((key) => (
                <tr
                  key={key.keyId}
                  data-testid={`key-row-${key.keyId}`}
                  className="border-b border-border last:border-b-0"
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-secondary">{key.prefix}</span>
                      <CopyButton
                        text={key.keyId}
                        label=""
                        copiedLabel=""
                        variant="inline"
                        className="p-1 text-muted transition-colors hover:bg-surface-elevated hover:text-secondary data-[copy-state=copied]:text-green-400"
                        title="Copy key ID"
                        aria-label={`Copy key ID ${key.keyId}`}
                        data-testid={`copy-key-${key.keyId}`}
                      />
                    </div>
                  </td>
                  <td className="px-4 py-3 text-secondary">{key.deviceName}</td>
                  <td className="px-4 py-3 text-muted">{formatRelativeDate(key.createdAt)}</td>
                  <td className="px-4 py-3 text-muted">{formatRelativeDate(key.lastUsed)}</td>
                  <td className="px-4 py-3">
                    {key.revoked ? (
                      <span className="border-l-2 border-red-500 pl-2 text-red-400">Revoked</span>
                    ) : (
                      <span className="border-l-2 border-green-500 pl-2 text-green-400">
                        Active
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {!key.revoked && revokingKeyId !== key.keyId && (
                      <button
                        type="button"
                        onClick={() => {
                          setRevokingKeyId(key.keyId);
                          setRevokeError(null);
                        }}
                        className="btn-press rounded-md border border-border px-3 py-1.5 text-small text-red-400 transition-colors hover:bg-red-500/10 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400"
                      >
                        Revoke
                      </button>
                    )}
                    {revokeSuccess === key.keyId && (
                      <span className="text-small text-green-400">Revoked</span>
                    )}
                    {revokingKeyId === key.keyId && (
                      <div className="flex items-center gap-2">
                        <span className="text-small text-muted">
                          Are you sure? This cannot be undone.
                        </span>
                        <button
                          type="button"
                          onClick={() => handleRevoke(key.keyId)}
                          className="btn-press rounded-md border border-red-500/50 px-2.5 py-1 text-small text-red-400 transition-colors hover:bg-red-500/10 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400"
                        >
                          Confirm
                        </button>
                        <button
                          type="button"
                          onClick={() => setRevokingKeyId(null)}
                          className="btn-press rounded-md border border-border px-2.5 py-1 text-small text-muted transition-colors hover:bg-surface-elevated focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cyan-400"
                        >
                          Cancel
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
